import { cloneElement, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';

import { Avatar } from '../Avatar';

type UserDropdownMenuItem = {
  key: string;
  label: string;
  icon: JSX.Element;
  dividerAfter?: boolean;
  onClick: () => void;
};

export type UserDropdownMenuProps = {
  role: string;
  name: string;
  avatar?: string;
  menuItems: UserDropdownMenuItem[];
};

export const UserDropdownMenu: React.FC<UserDropdownMenuProps> = (props) => {
  const { role, name, avatar, menuItems } = props;

  // ======================= STATE
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // ======================= EVENTS
  const onHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onHandleClose = () => {
    setAnchorEl(null);
  };

  // ======================= VARIABLES
  const open = Boolean(anchorEl);
  const id = open ? 'user-menu-popover' : undefined;
  const formatURL = (url) => url?.replace(/[\u200B-\u200D\uFEFF]/g, '');
  // ======================= VIEWS
  return (
    <>
      <IconButton onClick={onHandleClick} sx={{ padding: 0 }}>
        <Avatar
          alt={'avatar'}
          name={name}
          src={formatURL(avatar) || ''}
          avatarProps={{
            sx: { width: 32, height: 32, fontSize: 14 },
          }}
        />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onHandleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List>
          <ListItem
            sx={{
              pr: 16,
              py: 4,
            }}
          >
            <Avatar name={name} src={formatURL(avatar) || ''} alt="avatar" />
            <Box sx={{ ml: 2 }}>
              <Typography sx={{ fontSize: 14 }}>{name}</Typography>
              <Typography
                sx={{ fontSize: 12, fontWeight: 300, color: '#A0A0A0' }}
              >
                {role}
              </Typography>
            </Box>
          </ListItem>
          <Divider />
          {menuItems.map((e) => {
            const clonedIcon = cloneElement(e.icon, {
              fontSize: 'small',
            });
            return (
              <div key={e.key}>
                <ListItemButton
                  onClick={async () => {
                    onHandleClose();
                    e?.onClick();
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 2.5,
                      color: '#A0A0A0',
                      fontSize: 20,
                    }}
                  >
                    {clonedIcon}
                  </ListItemIcon>
                  <ListItemText
                    primary={e.label}
                    primaryTypographyProps={{
                      sx: { fontSize: 14 },
                    }}
                  />
                </ListItemButton>
                {e.dividerAfter && <Divider />}
              </div>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

// ======================= EXPORT
export default UserDropdownMenu;
